import React, { useState } from 'react'
import { Box, Typography, IconButton, Tooltip } from '@mui/material'
import {
  Description as DocumentIcon,
  Image as ImageIcon,
  PlayArrow as PlayArrowIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Link as LinkIcon,
  HideImage as HideImageIcon,
} from '@mui/icons-material'

import { ContentItem } from '../../types/catalog'

interface MediaPlaceholderProps {
  item: ContentItem
  isUploaded: boolean
  size?: 'small' | 'large'
}

const MediaPlaceholder: React.FC<MediaPlaceholderProps> = ({ item, isUploaded, size = 'small' }) => {
  const iconSize = size === 'large' ? 32 : 24
  const circleSize = size === 'large' ? 64 : 40
  const btnIconSize = size === 'large' ? 18 : 16

  const [copied, setCopied] = useState(false)

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    try {
      navigator.clipboard?.writeText(item.file_url || '')
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch (err) {}
  }

  const hasNoMedia = isUploaded && !item.preview_url && !item.file_url && !item.media_id

  const isLink = !isUploaded && !!item.file_url && size === 'large'

  return (
    <Box
      component={isLink ? 'a' : 'div'}
      href={isLink ? item.file_url : undefined}
      target={isLink ? '_blank' : undefined}
      rel={isLink ? 'noopener noreferrer' : undefined}
      onClick={isLink ? (e: React.MouseEvent) => e.stopPropagation() : undefined}
      sx={{
        position: 'absolute',
        top: 0, left: 0,
        width: '100%', height: '100%',
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        ...(isLink && !hasNoMedia && {
          '&:hover .preview-circle': {
            transform: 'translate(-50%, -50%) scale(1.08)',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          }
        })
      }}
    >
      <Box
        className="preview-circle"
        sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          ...(!hasNoMedia && {
            width: circleSize,
            height: circleSize,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(8px)',
            color: 'primary.main',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.9)',
          }),
        }}
      >
        {hasNoMedia ? (
          <>
            <HideImageIcon sx={{ fontSize: size === 'large' ? 32 : 22, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 500, letterSpacing: 0.3 }}>
              No media
            </Typography>
          </>
        ) : (
          item.type === 'document' ? <DocumentIcon sx={{ fontSize: iconSize }} /> :
          (item.type === 'video' || item.type === 'external_video') ? <PlayArrowIcon sx={{ fontSize: iconSize }} /> :
          <ImageIcon sx={{ fontSize: iconSize }} />
        )}
      </Box>

      {!isUploaded && item.file_url && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0, left: 0, right: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.75,
            bgcolor: 'transparent',
            backdropFilter: 'blur(8px)',
            borderTop: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <LinkIcon sx={{ fontSize: btnIconSize + 2, color: 'primary.main', flexShrink: 0 }} />
          <Tooltip title={item.file_url} placement="top">
            <Box
              component={isLink ? 'div' : 'a'}
              href={!isLink ? item.file_url : undefined}
              target={!isLink ? '_blank' : undefined}
              rel={!isLink ? 'noopener noreferrer' : undefined}
              onClick={!isLink ? (e: React.MouseEvent) => e.stopPropagation() : undefined}
              sx={{
                overflow: 'hidden',
                textDecoration: 'none',
                maxWidth: size === 'large' ? 340 : 160,
                minWidth: 0,
                '&:hover .link-text': {
                  textDecoration: 'underline',
                  textDecorationColor: 'primary.main',
                },
              }}
            >
              <Typography className="link-text" variant="caption" sx={{
                fontWeight: 600,
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                lineHeight: 1.4,
                transition: 'opacity 0.15s',
                '&:hover': { opacity: 0.8 },
              }}>
                {item.file_url.replace(/^https?:\/\/(www\.)?/, '')}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title={copied ? 'Copied!' : 'Copy link'} placement="top">
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                p: 0.25,
                color: 'text.disabled',
                position: 'relative',
                width: btnIconSize + 12,
                height: btnIconSize + 12,
                flexShrink: 0,
                '&:hover': { color: 'text.secondary', bgcolor: 'transparent' },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: btnIconSize, position: 'absolute', transition: 'opacity 0.2s', opacity: copied ? 0 : 1 }} />
              <CheckIcon sx={{ fontSize: btnIconSize, color: 'primary.main', position: 'absolute', transition: 'opacity 0.2s', opacity: copied ? 1 : 0 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  )
}

export default MediaPlaceholder
